// ColorField — a controlled colour picker: a swatch trigger that opens a Popover
// holding a curated palette grid + a free hex input. The "rich settings card"
// Popover case (COMPONENTS §Overlay patterns) on the React Aria foundation
// (ADR-029) — we own the markup, React Aria owns positioning / focus / escape /
// click-outside / RTL. Hand-rolling a 2D hue/sat area would need a drag engine +
// its own a11y story; a palette + hex covers brand-colour authoring (a brand hue
// comes from a guideline — usually a known hex), so this stays minimal + accessible.
//
// PURE "pick a colour" — no brand / derive / advisory knowledge. The brand model
// (auto-derived ink, contrast preview, semantic-clash advisory) lives in the
// BrandFields composite that consumes this. Reusable anywhere a colour is picked.

import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Popover } from './Popover.js';
import { Input } from './Input.js';
import { Label } from './Label.js';
import { icons } from './icons.js';
import { BRAND_PALETTE, normalizeHex, type BrandSwatch } from './brandPalette.js';
import { deriveOnPrimary } from '../../lib/oklch-fallback.js';

export interface ColorFieldProps {
  /** Current colour, `#rrggbb`. */
  value: string;
  /** Called with a normalized `#rrggbb` on palette pick or valid hex entry. */
  onChange: (hex: string) => void;
  /** Accessible name for the field (the visible <Label> text). */
  label: string;
  /** Palette shown in the popover. Defaults to the telecom-spectrum BRAND_PALETTE. */
  palette?: readonly BrandSwatch[];
  /** Field id (wires the label); auto-generated when omitted. */
  id?: string;
  /** Validation error under the trigger. */
  error?: string;
  /** Hint under the label. */
  help?: string;
}

export function ColorField({
  value,
  onChange,
  label,
  palette = BRAND_PALETTE,
  id,
  error,
  help,
}: ColorFieldProps) {
  const reactId = useId();
  const fieldId = id ?? reactId;
  const helpId = `${fieldId}-help`;
  const current = normalizeHex(value) ?? value;

  return (
    <div>
      <Label htmlFor={fieldId}>{label}</Label>
      {help && (
        <p id={helpId} className="mb-1 text-xs text-[var(--color-text-secondary)]">
          {help}
        </p>
      )}
      <Popover
        label={label}
        placement="bottom start"
        panelClassName="w-[18rem] p-3"
        renderTrigger={() => (
          <button
            id={fieldId}
            type="button"
            aria-label={`${label}: ${current}`}
            aria-describedby={help ? helpId : undefined}
            className="flex w-full items-center gap-2.5 rounded-md field-frame bg-[var(--color-bg-page)] px-2.5 py-2 text-start outline-none transition-colors"
          >
            <span
              aria-hidden
              className="h-6 w-6 shrink-0 rounded-md border border-[var(--color-border)]"
              style={{ backgroundColor: current }}
            />
            <span className="font-mono text-sm pointer-coarse:text-base uppercase text-[var(--color-text-primary)]">{current}</span>
            <icons.caretDown aria-hidden size={16} className="ms-auto text-[var(--color-text-secondary)]" />
          </button>
        )}
      >
        {(close) => (
          <PalettePanel
            palette={palette}
            value={current}
            onPick={(hex) => {
              onChange(hex);
              close();
            }}
            onHexCommit={onChange}
          />
        )}
      </Popover>
      {error && <p className="mt-1 text-xs text-[var(--color-danger)]">{error}</p>}
    </div>
  );
}

function PalettePanel({
  palette,
  value,
  onPick,
  onHexCommit,
}: {
  palette: readonly BrandSwatch[];
  value: string;
  onPick: (hex: string) => void;
  onHexCommit: (hex: string) => void;
}) {
  const { t } = useTranslation('common');
  const [hex, setHex] = useState(value);
  const [dirty, setDirty] = useState(false);
  const normalized = normalizeHex(hex);
  const invalid = dirty && normalized === null;

  return (
    <div className="flex flex-col gap-3">
      <div
        role="listbox"
        aria-label={t('colorField.paletteLabel')}
        className="grid grid-cols-8 gap-1.5"
      >
        {palette.map((s) => {
          const selected = s.hex === value;
          return (
            <button
              key={s.hex}
              type="button"
              role="option"
              aria-selected={selected}
              aria-label={`${s.name} · ${s.hex}`}
              title={`${s.name} · ${s.hex}`}
              onClick={() => onPick(s.hex)}
              className="flex aspect-square items-center justify-center rounded-md border border-[var(--color-border)] outline-none focus-ring transition-transform hover:scale-110"
              style={{ backgroundColor: s.hex }}
            >
              {selected && (
                <icons.check aria-hidden size={14} weight="bold" style={{ color: deriveOnPrimary(s.hex) }} />
              )}
            </button>
          );
        })}
      </div>
      <div>
        <Label htmlFor="colorfield-hex">{t('colorField.hexLabel')}</Label>
        <div className="flex items-center gap-2">
          <span
            aria-hidden
            className="h-8 w-8 shrink-0 rounded-md border border-[var(--color-border)]"
            style={{ backgroundColor: normalized ?? 'transparent' }}
          />
          <Input
            id="colorfield-hex"
            value={hex}
            error={invalid ? t('colorField.hexInvalid') : undefined}
            placeholder="#RRGGBB"
            className="font-mono uppercase"
            onChange={(e) => {
              const next = e.target.value;
              setHex(next);
              setDirty(true);
              const n = normalizeHex(next);
              if (n) onHexCommit(n);
            }}
          />
        </div>
      </div>
    </div>
  );
}
